import axios from "axios";
import { API_URL } from "const/constants";
import { getLockedTermIdsFromInstallments } from "./accountUtils";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export async function fetchTermLockInfo(clientId, studentDbId) {
  if (!clientId || !studentDbId) return { lockedIds: new Set(), rejectedIndices: new Set() };

  const lockedIds = new Set();
  const rejectedIndices = new Set();

  try {
    // Account installments — covers IMPORTED terms with paid amounts
    const accountRes = await axios.get(`${API_URL}/accounts/client/${clientId}`, {
      headers: authHeaders(),
    });
    const row = (accountRes?.data?.data?.accountDetails || []).find(
      (d) => d.module === "Admission" && d.admissionValues?.studentDbId === studentDbId
    );
    if (row?.admissionDetails?.length) {
      getLockedTermIdsFromInstallments(row.admissionDetails).forEach((id) => lockedIds.add(id));
    }
  } catch {}

  try {
    // Account verifications — covers PENDING/VERIFIED and identifies REJECTED
    const verRes = await axios.get(`${API_URL}/account-verification/client/${clientId}`, {
      headers: authHeaders(),
    });
    const verifications = verRes?.data?.data ?? [];
    const relevantVerifications = verifications.filter(
      (v) =>
        v.submittedAccountDetail?.admissionValues?.studentDbId === studentDbId ||
        v.submittedAccountDetail?.admissionValues?.studentDbId === Number(studentDbId)
    );

    // First pass: lock IDs and collect imported/verified indices
    const importedOrVerifiedIndices = new Set();
    relevantVerifications.forEach((v) => {
      (v.submittedAccountDetail?.admissionDetails || []).forEach((d) => {
        const termNum = parseInt(
          String(d.terms || "")
            .trim()
            .replace(/^[Tt]/, ""),
          10
        );
        if (Number.isNaN(termNum) || termNum < 1) return;
        if (["PENDING", "VERIFIED", "IMPORTED"].includes(v.status)) {
          lockedIds.add(String(termNum));
        }
        if (v.status === "IMPORTED" || v.status === "VERIFIED") {
          importedOrVerifiedIndices.add(termNum - 1);
        }
      });
    });

    // Second pass: only mark as rejected if no newer IMPORTED/VERIFIED exists
    relevantVerifications.forEach((v) => {
      if (v.status !== "REJECTED") return;
      (v.submittedAccountDetail?.admissionDetails || []).forEach((d) => {
        const termNum = parseInt(
          String(d.terms || "")
            .trim()
            .replace(/^[Tt]/, ""),
          10
        );
        if (Number.isNaN(termNum) || termNum < 1) return;
        if (!importedOrVerifiedIndices.has(termNum - 1)) {
          rejectedIndices.add(termNum - 1);
        }
      });
    });
  } catch {}

  return { lockedIds, rejectedIndices };
}

/**
 * Load paid/locked term ids for an admission from the client account API.
 * Used by Admissions offer-letter UI and the account eye drawer to disable paid terms.
 */
export async function fetchLockedTermIdsForStudent(clientId, studentDbId) {
  const { lockedIds } = await fetchTermLockInfo(clientId, studentDbId);
  return lockedIds;
}

export async function fetchAdmissionAccountLinkExists(clientId, studentDbId) {
  if (!clientId || !studentDbId) return false;
  try {
    const res = await axios.get(`${API_URL}/accounts/client/${clientId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    const account = res?.data?.data;
    return Boolean(
      account?.accountDetails?.some(
        (detail) =>
          detail.module === "Admission" && detail.admissionValues?.studentDbId === studentDbId
      )
    );
  } catch {
    return false;
  }
}
